import { AppShell, setUserIconColor } from 'components/AppShell';
import { signOut } from 'components/Login';
import { playersChangeCallback } from 'components/PieceSelector';
import { RegisterPlayer } from 'components/RegisterPlayer';
import { app, render, type JSXChild } from 'engine/index';
import { rpc, State } from 'engine/rpc';
import { showConfetti, showRedFlash } from 'engine/utils';
import { marked } from 'marked';
import type { GameId, GameUpdates } from 'shared/modelTypes';
import { translate } from 'shared/translate';
import { delayMs, lateinit, unreachable } from 'shared/util';

export abstract class Game<T extends GameId> {
	shell: HTMLElement = lateinit;

	shellContentEl!: HTMLElement;
	shellNameEl!: HTMLElement;
	userIconEl!: HTMLElement;

	abstract maxPlayers: number;

	constructor(updates: GameUpdates<T>[]) {
		this.createShell();
		render(app, this.shell);

		// HACK: for some reason typescript sets properties form the subclass back to undefined after our constructor logic runs
		// so we defer execution a bit
		queueMicrotask(() => {
			this.onShellCreated(this.shell);

			rpc.onGameUpdates.subscribe(
				{ sessionId: State.gameId.toString(), playerId: State.playerId },
				{
					onData: (update) => {
						// console.log('got update ', update);
						this.handleUpdate(update);
					},
				},
			);

			for (const update of updates) {
				this.handleUpdate(update);
			}
		});
	}

	abstract getGameName(): string;
	abstract defineBottomUI(): JSXChild;
	abstract handleUpdate(update: GameUpdates<T>): void;

	createShell() {
		this.shell = (
			<AppShell //
				gameName={this.getGameName()}
				bottomUI={this.defineBottomUI()}
			/>
		);
	}

	onShellCreated(view: HTMLElement) {
		this.shellNameEl = view.querySelector('[user-name]')!;
		this.shellContentEl = view.querySelector('[shell-content]')!;
		this.userIconEl = view.querySelector('[user-icon]')!;
	}

	triggerEffect(update: { kind: 'trigger-effect'; effect: 'confetti' | 'red-flash'; text?: string; parameters?: string[] }) {
		if (update.text) {
			const text: HTMLElement = (
				<div class="w-full text-center text-2xl font-bold text-gray-950">{translate(update.text, update.parameters)}</div>
			);
			text.innerHTML = marked.parse(translate(update.text, update.parameters)) as string;

			const overlay: HTMLElement = (
				<div class="fixed inset-0 z-[3] flex max-h-screen items-center justify-center overflow-hidden bg-white/40 p-8">{text}</div>
			);
			document.body.append(overlay);
			overlay.animate([{ opacity: '0' }, { opacity: '1' }], { duration: 200, easing: 'ease-out' });
			text.animate([{ transform: 'scale(2) rotate(40deg)' }, { transform: 'scale(1) rotate(0deg)' }], {
				duration: 400,
				easing: 'ease-out',
			}).finished.then(async () => {
				await delayMs(2000);

				text.animate(
					[
						{ transform: 'scale(1)', opacity: '1' },
						{ transform: 'scale(2)', opacity: '0' },
					],
					{ duration: 200, easing: 'ease-out', fill: 'forwards' },
				);
				overlay.animate([{ opacity: '1' }, { opacity: '0' }], { duration: 400, easing: 'ease-out' }).finished.then(() => {
					overlay.remove();
				});
			});
		}

		// prettier-ignore
		switch (update.effect) {
				case 'confetti': showConfetti(); break;
				case 'red-flash': showRedFlash(); break;
				default: unreachable(update.effect);
			}
	}

	showLoginScreen() {
		signOut();
	}

	showRegisterScreen(playAs: 'player' | 'team' = 'player', colors: string[] | undefined = undefined, numberMode: boolean = false, extraField: boolean = false) {
		render(
			this.shellContentEl,
			<RegisterPlayer
				playAs={playAs}
				name={State.name ?? ''}
				color={State.color ?? ''}
				colors={colors}
				maxPlayers={this.maxPlayers}
				numberMode={numberMode}
				extraField={extraField}
				onChange={(name, color, companyOrFunction) => {
					State.name = name;
					State.color = color;

					this.shellNameEl.innerText = name;
					setUserIconColor(this.userIconEl, color);

					const player = {
						gameId: State.gameId,
						playerId: State.playerId,
						name: name,
						color: color,
						companyOrFunction: companyOrFunction,
					};
					rpc.updatePlayer.query(player);
				}}
				onLeave={() => {
					const player = {
						gameId: State.gameId,
						playerId: State.playerId,
					};
					rpc.removePlayer.query(player);
				}}
			/>,
		);
	}

	updatePlayersState(update: { kind: 'update-players-state'; players: { name: string; color: string }[]; numberOfPlayers?: number }) {
		if (playersChangeCallback) {
			playersChangeCallback(update.players);
		}
	}
}
